<template>
  <div>

    <div class="panel">
      <b-button v-b-modal.help-modal>Help</b-button>

      <b-button
        :disabled="selectedLimits.length === 0"
        @click="deleteSelectedLimits"
        :variant="selectedLimits.length === 0 ? 'light' : 'danger'"
      >
        Delete
      </b-button> 
    </div>

    <loading-indicator
      v-if="isLoading"
    />

    <p v-if="message !== null">
      {{message}}
    </p>

    <table v-if="limits !== null && limits.length > 0" class="table text-nowrap">
      <thead class="thead-dark">
        <tr>
          <th scope="col">
            <b-form-checkbox
              v-model="allSelected"
              @change="toggleSelection"
            >
              Limit
            </b-form-checkbox>
          </th>
          <th scope="col">Arg</th>
          <th scope="col" class="text-right">Value</th>
          <th scope="col" class="text-right">Max</th>
          <th scope="col">Begin</th>
          <th scope="col">Duration</th>
          <th scope="col">End</th>
          <th scope="col">Action</th>
        </tr>
      </thead>
      <tr
        v-for="limit in limits"
        :key="limit.packed_id"
        :class="getLimitClass(limit)"
      >
        <td>
          <b-form-checkbox
            v-model="selectedLimits"
            :value="limit"
          >
            {{limit.id | getEnumLimitName}}
          </b-form-checkbox>
        </td>
        <td>
          <player-link
            v-if="limit.id === LIMIT_CLUB_SEND_INVITE"
            :playerId="limit.arg"
            v-b-popover.hover.top="'Player who was invited'"
          />
          <player-link
            v-else-if="limit.id === LIMIT_CLUB_RESPOND_HELP_MSG"
            :playerId="limit.arg"
            v-b-popover.hover.top="'Player who was helped'"
          />
          <player-link
            v-else-if="limit.id === LIMIT_CLUB_COMPLAIN_AT_USER"
            :playerId="limit.arg"
            v-b-popover.hover.top="'Complainee'"
          />
          <club-link
            v-else-if="limit.id === LIMIT_CLUB_PRIV_CLUB_JOIN_REQUEST"
            :clubId="limit.arg"
            v-b-popover.hover.top="'Join request was sent to this club.'"
          />
          <player-link
            v-else-if="limit.id === LIMIT_CLUB_SEND_FRIEND_GIFT"
            :playerId="limit.arg"
            v-b-popover.hover.top="'Gift was sent to this player.'"
          />
          <template
            v-else-if="limit.arg !== 0"
          >
            {{limit.arg}}
          </template>
        </td>
        <td
          class="text-right"
          :class="getValueClass(limit)"
        >
          {{limit.value}}
        </td>
        <td class="text-right">
          {{getMax(limit.id)}}
        </td>
        <td>
          {{limit.mtime | getMomentStr}}
        </td>
        <td>
          {{getLimitDurationStr(limit.id)}}
        </td>
        <td>
          {{getEndStr(limit)}}
        </td>
        <td>
          <b-button @click='deleteLimit(limit)' variant="danger" size="sm">Delete</b-button>
        </td>
      </tr>
    </table>

    <b-modal id="help-modal" title="Справка по лимитам" ok-only ok-title="Close">
      <div class="help">
        <p>
          Лимиты, время действия которых завершилось, показываются серым цветом.
        </p>
        <p>
          Для активных лимитов (время действия которых не&nbsp;закончилось) значение выводится разным цветом.
          Если значение меньше максимума, то оно показывается <span class="canInc">зеленым</span> цветом.
          Если максимум достигнут, то значение показывается <span class="isMax">красным</span> цветом.
        </p>
      </div>
    </b-modal>

  </div>
</template>

<script>
import { HTTP } from '../http-common';
import notifier from '../notifier';
import LoadingIndicator from './LoadingIndicator.vue';
import PlayerLink from './PlayerLink.vue';
import ClubLink from './ClubLink.vue';
import { getMomentStr } from '../util';
import { getDurationStrSimple } from '../util';
import { getEnumLimitName } from '../autogen/bundle';


const LIMIT_NONE                           =  0;

const LIMIT_CLUB_SEND_INVITE               =  9; // Arg is player id. Invitaion was sent to this player.
const LIMIT_CLUB_ASK_HELP                  = 10;
const LIMIT_CLUB_RESPOND_HELP_MSG          = 11;
const LIMIT_CLUB_UI_OPENED_FIRST_TIME      = 12;
const LIMIT_CLUB_FIRST_TIME_JOIN           = 13;
const LIMIT_CLUB_JOIN_UI_OPENED_FIRST_TIME = 14;
const LIMIT_CLUB_COMPLAIN_AT_USER          = 15; // Arg is complainee id.
const LIMIT_CLUB_COMPLAINS_PER_DAY         = 16;
const LIMIT_CLUB_PRIV_CLUB_JOIN_REQUEST    = 17; // Arg is club id.
const LIMIT_CLUB_PRIV_JOINS_REQS_PER_DAY   = 18;
const LIMIT_CLUB_SEND_FRIEND_GIFT          = 19; // Arg is user id we are sending gift to.

const LIMIT_ADS_VIDEO_DAILY_TOTAL          = 20;
const LIMIT_ADS_VIDEO_PLACEMENT_COOLDOWN   = 30;

const LIMIT_NOTIFICATIONS_LOCAL_DAILY      = 40;


export default {
  name: 'PlayerLimits',

  components: {
    LoadingIndicator,
    PlayerLink,
    ClubLink,
  },

  props: {
    playerId: {
      type: Number,
      required: true,
    }
  },

  data() {
    return {
      LIMIT_NONE,

      LIMIT_CLUB_SEND_INVITE,
      LIMIT_CLUB_ASK_HELP,
      LIMIT_CLUB_RESPOND_HELP_MSG,
      LIMIT_CLUB_UI_OPENED_FIRST_TIME,
      LIMIT_CLUB_FIRST_TIME_JOIN,
      LIMIT_CLUB_JOIN_UI_OPENED_FIRST_TIME,
      LIMIT_CLUB_COMPLAIN_AT_USER,
      LIMIT_CLUB_COMPLAINS_PER_DAY,
      LIMIT_CLUB_PRIV_CLUB_JOIN_REQUEST,
      LIMIT_CLUB_PRIV_JOINS_REQS_PER_DAY,
      LIMIT_CLUB_SEND_FRIEND_GIFT,

      LIMIT_ADS_VIDEO_DAILY_TOTAL,
      LIMIT_ADS_VIDEO_PLACEMENT_COOLDOWN,

      LIMIT_NOTIFICATIONS_LOCAL_DAILY,

      isLoading: false,
      conf: null,
      limits: null,

      selectedLimits: [],
      allSelected: false,

      timerId: null,
      nowSec: Date.now() / 1000,
    };
  },

  computed: {
    message() {
      if(this.limits !== null && this.limits.length === 0) {
        return 'This player has no limits.';
      }
      return null;
    },
  },

  watch: {
    selectedLimits(newValue) {
      this.allSelected = (newValue.length === this.limits.length);
    },
  },

  created() {
    this.timerId = setInterval(this.updateTime, 1000);

    this.isLoading = true;

    const url = `/player/${this.playerId}/limits`;
    HTTP.get(url)
    .then(response => {
      const data = response.data.data;
      if(data === null) {
        notifier.showError('Invalid server response. data === null.', {sender: this});
        return;
      }

      this.conf = data.conf;

      const limits = data.limits.slice();
      limits.sort((a, b) => a.id - b.id);
      this.limits = limits;
    })
    .catch(e => {
      notifier.showError(e.response.data);
    })
    .finally(() => {
      this.isLoading = false;
    });
  },

  beforeDestroy() {
    if(this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  },

  methods: {
    updateTime() {
      this.nowSec = Date.now() / 1000;
    },

    getEndStr(limit) {
      const conf = this.getLimitConf(limit.id);
      if(conf === null) {
        return '?';
      }

      if(conf.ttl === -1) {
        return 'endless';
      }

      const end = limit.mtime + conf.ttl;
      return getMomentStr(end);
    },

    getEnd(limit) {
      const limitConf = this.getLimitConf(limit.id);
      if(limitConf === null) {
        return null;
      }
      return this.getEndByLimitConf(limit, limitConf);
    },

    getEndByLimitConf(limit, conf) {
      if(conf.ttl === -1) {
        return null;
      }
      const end = limit.mtime + conf.ttl;
      return end;
    },

    getMaxStr(limitId) {
      const max = this.getMax(limitId);
      if(max === null) {
        return '?';
      }
      return max.toString();
    },

    getMax(limitId) {
      const limitConf = this.getLimitConf(limitId);
      if(limitConf === null) {
        return null;
      }
      return limitConf.max;
    },

    getLimitConf(limitId) {
      for(var i = 0; i < this.conf.items.length; i++) {
        const limitConf = this.conf.items[i];
        if(limitConf.id === limitId) {
          return limitConf;
        }
      }
      return null;
    },

    getLimitClass(limit) {
      const end = this.getEnd(limit);
      if(end === null) {
        return {};
      }

      const isEnded = this.nowSec >= end;
      return {
        ended: isEnded,
      };
    },

    getLimitDurationStr(limitId) {
      const limitConf = this.getLimitConf(limitId);
      if(limitConf === null) {
        return '?';
      }

      if(limitConf.ttl === -1) {
        return 'forever';
      }

      return getDurationStrSimple(limitConf.ttl);
    },

    getValueClass(limit) {
      const limitConf = this.getLimitConf(limit.id);
      if(limitConf === null) {
        return {};
      }

      const end = this.getEndByLimitConf(limit, limitConf);
      const isEnded = (end !== null && this.nowSec >= end);
      if(isEnded) {
        return {};
      }

      const isMax = limit.value >= limitConf.max;
      return {
        canInc: !isMax,
        isMax: isMax,
      };
    },

    toggleSelection(checked) {
      if(checked) {
        this.selectedLimits = this.limits.slice();
      } else {
        this.selectedLimits = [];
      }
    },

    deleteLimit(limit) {
      notifier.deleteSenderNotifications(this);

      const data = new FormData();
      data.append('id', limit.id);
      data.append('arg', limit.arg);

      const url = `/player/${this.playerId}/limit/del`;
      HTTP.post(url, data)
      .then(() => {
        const index = findLimitIndex(this.limits, limit);
        if(index !== -1) {
          this.limits.splice(index, 1);
        }
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    deleteSelectedLimits() {
      if(this.selectedLimits.length === 0) {
        return;
      }

      notifier.deleteSenderNotifications(this);

      const url = `/player/${this.playerId}/limits/del`;
      const limitInstanceIds = getLimitInstanceIds(this.selectedLimits);

      HTTP.post(url, limitInstanceIds)
      .then(() => {
        delLimitsByInstanceIds(this.limits, limitInstanceIds);
        this.selectedLimits.length = 0;
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },
  },

  filters: {
    getEnumLimitName,
  },
};

function delLimitsByInstanceIds(limits, limitInstanceIds) {
  for(const limitInstanceId of limitInstanceIds) {
    delLimitByInstanceId(limits, limitInstanceId);
  }
}

function delLimitByInstanceId(limits, limitInstanceId) {
  const index = findLimitIndex(limits, limitInstanceId);
  if(index !== -1) {
    limits.splice(index, 1);
  }
}

function findLimitIndex(limits, limitInstanceId) {
  for(let i = 0; i < limits.length; i++) {
    const cur = limits[i];
    if(cur.id === limitInstanceId.id && cur.arg === limitInstanceId.arg) {
      return i;
    }
  }
  return -1;
}

function getLimitInstanceIds(limits) {
  return limits.map(getLimitInstanceId);
}

function getLimitInstanceId(limit) {
  return {
    id:  limit.id,
    arg: limit.arg,
  };
}
</script>

<style scoped>
  .panel {
    margin-bottom: 1em;
  }

  .ended {
    /*color: #8fa1b2;*/
    color: #dcdcdc;
  }

  .canInc {
    color: var(--green);
  }

  .isMax {
    color: var(--red);
  }

  .help p {
    margin-bottom: 1em;
  }
</style>
